import { useEffect, useState } from 'react'
import { classNames, legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorRecipientSnapshot } from '@/msgdeskRuntime/contracts/editorActions'
import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import { useEditorSession } from '../../EditorSessionContext'
import { useEditorSurface } from '../../EditorSurfaceContext'

type RecipientKind = 'email' | 'msg' | 'self'

type RecipientToken = Pick<EditorRecipientSnapshot, 'id' | 'label' | 'kind' | 'editable'>

function collectTokens(): RecipientToken[] {
  return dispatchEditorAction({ action: 'collect-recipients' }).map(({ id, label, kind, editable }) => ({ id, label, kind, editable }))
}

export function RecipientList() {
  const { skin } = useEditorSurface()
  const editorSession = useEditorSession()
  const isUpdate = editorSession.mode === 'update_msg'
  const [tokens, setTokens] = useState<RecipientToken[]>(() => collectTokens())

  useEffect(() => {
    const runtime = getMsgDeskRuntime()
    const syncTokens = () => setTokens(collectTokens())

    const handleInsert = (event: Event) => {
      const detail = (event as CustomEvent<{ label?: string; kind?: RecipientKind }>).detail
      const label = String(detail?.label || '').trim()
      if (!label) return syncTokens()
      runtime.setTimeout(syncTokens, 0)
    }

    const id = runtime.setTimeout(syncTokens, 0)
    runtime.addEventListener('msg:v2-recipient-insert', handleInsert)
    runtime.addEventListener('msg:v2-recipient-sync', syncTokens)
    return () => {
      runtime.clearTimeout(id)
      runtime.removeEventListener('msg:v2-recipient-insert', handleInsert)
      runtime.removeEventListener('msg:v2-recipient-sync', syncTokens)
    }
  }, [])

  const removeToken = (tokenId: number) => {
    dispatchEditorAction({ action: 'remove-recipient', id: tokenId })
    setTokens(current => current.filter(token => token.id !== tokenId))
  }

  return (
    <div {...legacyHookAttrs('editor.recipients.list', classNames('md-to__list', skin.recipients.list))}>
      {tokens.map(token => {
        const editable = token.editable && !isUpdate
        return (
          <span
            key={token.id}
            className={classNames(
              'md-to__token',
              skin.recipients.token,
              token.kind === 'email' && 'md-to__token--email',
              token.kind === 'email' && skin.recipients.tokenKind.email,
              token.kind === 'msg' && 'md-to__token--msg',
              token.kind === 'msg' && skin.recipients.tokenKind.msg,
              token.kind === 'self' && 'md-to__token--self',
              token.kind === 'self' && skin.recipients.tokenKind.self,
              // Existing non-editable tokens and every sent/update recipient
              // keep their identity but expose no remove action.
              !editable && 'md-to__token--locked',
              !editable && skin.recipients.tokenLocked,
            )}
          >
            <span className={classNames('md-to__token-text', skin.recipients.tokenText, token.kind === 'self' && skin.recipients.tokenTextSelf)}>{token.label}</span>
            {editable ? (
              <span
                className={classNames('md-to__token-x', skin.recipients.tokenX, token.kind === 'self' && skin.recipients.tokenXSelf)}
                onClick={() => removeToken(token.id)}
              >
                ×
              </span>
            ) : null}
          </span>
        )
      })}
    </div>
  )
}
